import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { organizationsApi, adminApi } from '../services/api';
import { Organization, SubscriptionTier, SubscriptionStatus, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './UserManager.css';

interface OrgFormState {
  name: string;
  slug: string;
  description: string;
  subscription_tier: SubscriptionTier;
  subscription_status: SubscriptionStatus;
  is_active: boolean;
}

interface OrgEditFormState extends OrgFormState {}

interface AssignmentFormState {
  user_id: number;
  organization_id: number;
}

function OrganizationManager() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgSubmitting, setOrgSubmitting] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [clearingData, setClearingData] = useState<number | null>(null);

  const initialOrgForm: OrgFormState = {
    name: '',
    slug: '',
    description: '',
    subscription_tier: 'free',
    subscription_status: 'active',
    is_active: true,
  };

  const [orgForm, setOrgForm] = useState<OrgFormState>(initialOrgForm);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [orgEditForm, setOrgEditForm] = useState<OrgEditFormState>(initialOrgForm);
  const [assignmentForm, setAssignmentForm] = useState<AssignmentFormState>({ user_id: 0, organization_id: 0 });

  const isSuperAdmin = Boolean(user?.role === 'superadmin' || user?.is_superuser);
  const canCreateOrganizations = isSuperAdmin;
  const canAssignUsers = isSuperAdmin;

  const tierDetails = useMemo(
    () => [
      {
        key: 'free' as SubscriptionTier,
        label: 'Free',
        description: 'Phase 1 feature set � manual uploads, customer mapping, CSV exports.',
      },
      {
        key: 'basic' as SubscriptionTier,
        label: 'Basic',
        description: 'Adds Microsoft 365 data + richer export formats from Phase 2 roadmap.',
      },
      {
        key: 'premium' as SubscriptionTier,
        label: 'Premium',
        description: 'Unlocks automated data feeds and invoice workflows from Phases 3-5.',
      },
      {
        key: 'enterprise' as SubscriptionTier,
        label: 'Enterprise',
        description: 'Custom enterprise agreements; preserves compatibility with legacy customers.',
      },
    ],
    []
  );

  const statusOptions: { value: SubscriptionStatus; label: string }[] = [
    { value: 'active', label: 'Active � full platform access' },
    { value: 'suspended', label: 'Suspended � logins allowed, uploads blocked' },
    { value: 'cancelled', label: 'Cancelled � read-only retention' },
  ];

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationsApi.list();
      setOrganizations(data);
      setAssignmentForm(prev => {
        if (!prev.organization_id && data.length) {
          return { ...prev, organization_id: data[0].id };
        }
        return prev;
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await adminApi.listUsers(0, 1000);
      setUsers(data);
      setAssignmentForm(prev => {
        if (!prev.user_id && data.length) {
          return { ...prev, user_id: data[0].id };
        }
        return prev;
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load users');
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    if (canAssignUsers) {
      loadUsers();
    }
  }, [canAssignUsers, loadUsers]);

  const handleOrgFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value } = target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    const nextValue = isCheckbox ? target.checked : value;
    setOrgForm(prev => {
      const next = { ...prev, [name]: nextValue };
      if (name === 'name' && !prev.slug) {
        next.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }
      return next;
    });
  };

  const handleOrgEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value } = target;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
    const nextValue = isCheckbox ? target.checked : value;
    setOrgEditForm(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleAssignmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAssignmentForm(prev => ({ ...prev, [name]: Number(value) }));
  };

  const submitOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateOrganizations) {
      toast.error('Only super admins can create organizations');
      return;
    }
    if (!orgForm.name || !orgForm.slug) {
      toast.error('Name and slug are required');
      return;
    }

    setOrgSubmitting(true);
    try {
      await organizationsApi.create({
        name: orgForm.name,
        slug: orgForm.slug,
        description: orgForm.description || undefined,
        subscription_tier: orgForm.subscription_tier,
        subscription_status: orgForm.subscription_status,
        is_active: orgForm.is_active,
      });
      toast.success('Organization created');
      setOrgForm(initialOrgForm);
      await loadOrganizations();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Failed to create organization');
    } finally {
      setOrgSubmitting(false);
    }
  };

  const openOrgEdit = (org: Organization) => {
    setEditingOrg(org);
    setOrgEditForm({
      name: org.name,
      slug: org.slug,
      description: org.description || '',
      subscription_tier: org.subscription_tier,
      subscription_status: org.subscription_status,
      is_active: org.is_active,
    });
  };

  const closeOrgEdit = () => {
    setEditingOrg(null);
    setOrgEditForm(initialOrgForm);
    setOrgSaving(false);
  };

  const handleClearData = async (orgId: number, orgName: string) => {
    const confirmMessage = `Are you sure you want to clear ALL data for organization "${orgName}"?\n\nThis will delete:\n- All customers and their aliases\n- All invoice imports and product records\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Prompt for password confirmation (step-up authentication)
    const password = window.prompt('Please enter your password to confirm this action:');
    if (!password) {
      toast.error('Password required to clear organization data');
      return;
    }

    setClearingData(orgId);
    try {
      const result = await organizationsApi.clearData(orgId, password, true, true);
      
      const deletedItems = [];
      if (result.deleted.customers > 0) {
        deletedItems.push(`${result.deleted.customers} customers`);
      }
      if (result.deleted.customer_aliases > 0) {
        deletedItems.push(`${result.deleted.customer_aliases} aliases`);
      }
      if (result.deleted.products > 0) {
        deletedItems.push(`${result.deleted.products} products`);
      }
      if (result.deleted.uploads > 0) {
        deletedItems.push(`${result.deleted.uploads} uploads`);
      }
      
      toast.success(
        `Cleared all data for ${result.organization_name}: ${deletedItems.join(', ')}`
      );
      // Optionally reload organizations to refresh any counts
      loadOrganizations();
    } catch (error: any) {
      console.error('Failed to clear organization data:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear organization data');
    } finally {
      setClearingData(null);
    }
  };

  const submitOrgEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;
    setOrgSaving(true);
    try {
      await organizationsApi.update(editingOrg.id, {
        ...orgEditForm,
        description: orgEditForm.description || undefined,
      });
      toast.success('Organization updated');
      await loadOrganizations();
      closeOrgEdit();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Failed to update organization');
      setOrgSaving(false);
    }
  };

  const submitAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAssignUsers) {
      toast.error('Only super admins can reassign organizations');
      return;
    }
    if (!assignmentForm.user_id || !assignmentForm.organization_id) {
      toast.error('Select user and organization');
      return;
    }

    setAssignSubmitting(true);
    try {
      await adminApi.updateUser(assignmentForm.user_id, {
        organization_id: assignmentForm.organization_id,
      });
      toast.success('User organization updated');
      await loadUsers();
      await loadOrganizations();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Failed to update user organization');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const getOrganizationName = (orgId: number | null | undefined) => {
    if (!orgId) return 'Unassigned';
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : `Org #${orgId}`;
  };

  if (!canAssignUsers) {
    return (
      <div className="card">
        <h2>Access Restricted</h2>
        <p>You need super administrator privileges to manage organizations.</p>
      </div>
    );
  }

  return (
    <div className="user-manager">
      <h1>Organization Management</h1>

      {canCreateOrganizations && (
        <div className="card create-card">
          <h2>Create Organization</h2>
          <form onSubmit={submitOrganization} className="create-form">
            <div className="form-row">
              <label>Name</label>
              <input name="name" value={orgForm.name} onChange={handleOrgFormChange} />
            </div>
            <div className="form-row">
              <label>Subscription status</label>
              <select name="subscription_status" value={orgForm.subscription_status} onChange={handleOrgFormChange}>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Slug</label>
              <input name="slug" value={orgForm.slug} onChange={handleOrgFormChange} />
            </div>
            <div className="form-row">
              <label>Subscription tier</label>
              <select name="subscription_tier" value={orgForm.subscription_tier} onChange={handleOrgFormChange}>
                {tierDetails.map(tier => (
                  <option key={tier.key} value={tier.key}>{tier.label}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Description</label>
              <textarea name="description" rows={3} value={orgForm.description} onChange={handleOrgFormChange} />
            </div>
            <div className="form-row">
              <button className="btn btn-primary" type="submit" disabled={orgSubmitting}>
                {orgSubmitting ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </form>
          <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#555' }}>
            <strong>Tier overview</strong>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
              {tierDetails.map(tier => (
                <li key={tier.key}>
                  <strong>{tier.label}:</strong> {tier.description}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card create-card">
        <h2>Assign Existing User to Organization</h2>
        <form onSubmit={submitAssignment} className="create-form">
          <div className="form-row">
            <label>User</label>
            <select name="user_id" value={assignmentForm.user_id} onChange={handleAssignmentChange}>
              <option value={0}>Select user...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.email} (Current: {getOrganizationName(u.organization_id)})
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Organization</label>
            <select name="organization_id" value={assignmentForm.organization_id} onChange={handleAssignmentChange}>
              <option value={0}>Select organization...</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <button className="btn btn-primary" type="submit" disabled={assignSubmitting}>
              {assignSubmitting ? 'Assigning...' : 'Assign User'}
            </button>
          </div>
        </form>
      </div>

      <div className="users-list card">
        <h2>Organizations ({organizations.length})</h2>
        {loading ? (
          <div className="loading">Loading organizations...</div>
        ) : organizations.length === 0 ? (
          <p>No organizations found</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Active</th>
                <th>Users</th>
                <th>Created</th>
                {canCreateOrganizations && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {organizations.map(org => (
                <tr key={org.id}>
                  <td>{org.name}</td>
                  <td>{org.slug}</td>
                  <td>{org.subscription_tier}</td>
                  <td>{org.subscription_status}</td>
                  <td>{org.is_active ? 'Yes' : 'No'}</td>
                  <td>{org.user_count ?? 0}</td>
                  <td>{new Date(org.created_at).toLocaleDateString()}</td>
                  {canCreateOrganizations && (
                    <td>
                      <button className="btn btn-outline btn-sm" type="button" onClick={() => openOrgEdit(org)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-outline btn-sm"
                        type="button"
                        onClick={() => handleClearData(org.id, org.name)}
                        disabled={clearingData === org.id}
                        style={{ marginLeft: 8, color: '#dc2626', borderColor: '#dc2626' }}
                        title="Clear all customers, invoices, and related data for this organization"
                      >
                        {clearingData === org.id ? 'Clearing...' : 'Clear All Data'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canCreateOrganizations && editingOrg && (
        <div className="modal-overlay" onClick={closeOrgEdit}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Edit organization: {editingOrg.name}</h3>
            <form onSubmit={submitOrgEdit} className="edit-form">
              <div className="form-row">
                <label>Name</label>
                <input name="name" value={orgEditForm.name} onChange={handleOrgEditChange} />
              </div>
              <div className="form-row">
                <label>Subscription status</label>
                <select name="subscription_status" value={orgEditForm.subscription_status} onChange={handleOrgEditChange}>
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Slug</label>
                <input name="slug" value={orgEditForm.slug} onChange={handleOrgEditChange} />
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea name="description" rows={3} value={orgEditForm.description} onChange={handleOrgEditChange} />
              </div>
              <div className="form-row">
                <label>Subscription tier</label>
                <select name="subscription_tier" value={orgEditForm.subscription_tier} onChange={handleOrgEditChange}>
                  {tierDetails.map(tier => (
                    <option key={tier.key} value={tier.key}>{tier.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row modal-actions">
                <button className="btn btn-primary" type="submit" disabled={orgSaving}>
                  {orgSaving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-outline" style={{ marginLeft: 8 }} onClick={closeOrgEdit}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default OrganizationManager;
